import { Button, Checkbox, Select, Spin, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CommonIcons from "../../assets/icons";
import CardItemInCart from "../../components/VMRS/Cart/CardItemInCart";
import { MapContext } from "../../components/VMRS/MapAdapter/core/MapContext";
import { UniversalMap } from "../../components/VMRS/MapAdapter/UniversalMap";
import ContentLayoutManagement from "../../layouts/ContentLayoutManagement";
import HeaderContent from "../../layouts/HeaderContent";
import
{
   delImgIntoCartAsync,
   getImgsAsync,
   setArrIdImage,
   setSelectedItems,
} from "../../store/reducers/cartSlice";
import { RootState } from "../../store/store";
import
{
   flyToGeometries,
   removeFootprint,
   removeImageFromMap,
   showFootprint,
   showImageOnMap,
} from "../../components/VMRS/ExploreArchive/ultis";
import useToggle from "../../hooks/useToggle";
import CommonComponents from "../../components/Commons";
import { showError, showSuccess } from "../../helpers/toast";
import ImageInfo from "../../components/VMRS/ExploreArchive/ImageInfo";
import NoData from "../../components/NoData";
import { useNavigate } from "react-router-dom";
import { clearAllLayers } from "../../components/VMRS/MapAdapter/store/slices/layerSlice";
import { clearAOIFeatures, setDrawingAOIMode } from "../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { setDrawingMode } from "../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { setMeasureMode } from "../../components/VMRS/MapAdapter/store/slices/measureSlice";

export default function CartPage()
{
   const { t } = useTranslation();
   const dispatch = useDispatch();

   const [loading, setLoading] = useState(false);
   // const [selected, setSelected] = useState<any[]>([]);
   const [engine, setEngine] = useState<any>(null);
   const [allData, setAllData] = useState<any[]>([]);
   const [data, setData] = useState<any>([]);
   const [selectedView, setSelectedView] = useState(undefined);
   const [providerId, setProviderId] = useState<string>("all");
   const [singleDeleteItem, setSingleDeleteItem] = useState<any>(null);

   const selected = useSelector(
      (state: RootState) => state.cartSlice.selectedItems,
   );
   const arrIdImage = useSelector(
      (state: RootState) => state.cartSlice.arrIdImage,
   );
   const navigate = useNavigate();

   const layersOnMap = useSelector((state: RootState) => state.layer.layers);

   const total = useMemo(() =>
   {
      return arrIdImage?.length;
   }, [arrIdImage]);

   const providers = useSelector(
      (state: RootState) => state.appCommonSlice.allProviders,
   );

   const {
      open: openConfirmDelete,
      shouldRender: shouldRenderConfirmDelete,
      toggle: toggleConfirmDelete,
   } = useToggle();

   const {
      open: openSingleDelete,
      shouldRender: shouldRenderSingleDelete,
      toggle: toggleSingleDelete,
   } = useToggle();

   const {
      open: openViewInfo,
      shouldRender: shouldRenderViewInfo,
      toggle: toggleViewInfo,
   } = useToggle();

   const getData = async () =>
   {
      setLoading(true);
      try
      {
         // @ts-ignore
         let res = await dispatch(
            // @ts-ignore
            getImgsAsync({
               provider_id: providerId === "all" ? undefined : providerId,
            }),
         ).unwrap();

         // Always keep arrays so downstream (providerCountMap.forEach) never
         // crashes the screen when the API fails / returns no items.
         const items = res?.data?.data?.items ?? [];

         const arr = items.filter((p: any) =>
            providerId === "all"
               ? true
               : p?.stac_item?.provider?.id === Number(providerId),
         );

         setAllData(items);
         setData(arr);
      } catch (error)
      {
         showError(t("Failed to load cart"));
         setAllData([]);
         setData([]);
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      getData();
   }, [providerId]);

   // ================= RESET MAP STATE ON MOUNT/UNMOUNT =================
   // Avoid layer-state leaking across pages: when navigating in/out of cart,
   // any footprints/images shown previously must be cleared so the dropdown
   // labels (Show/Hide) match what is actually rendered on the new map.
   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearAllLayers());
      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      handleResetMap();
      return () =>
      {
         handleResetMap();
      };
   }, []);

   // ================= SELECTION LOGIC =================

   // chỉ tính selected trong view hiện tại
   const selectedInView = useMemo(() =>
   {
      return selected.filter((s: any) =>
         data.some((d: any) => d.stac_item_id === s.id),
      );
   }, [selected, data]);

   const isAllSelected =
      data.length > 0 && selectedInView.length === data.length;

   const isIndeterminate =
      selectedInView.length > 0 && selectedInView.length < data.length;
   const layerIds = useMemo(
      () => new Set(layersOnMap.map((l: any) => l.id)),
      [layersOnMap],
   );

   const toggleItem = (item: any) =>
   {
      dispatch(
         setSelectedItems(
            selected.some((p: any) => p.id === item.id)
               ? selected.filter((p: any) => p.id !== item.id)
               : [...selected, item],
         ),
      );
   };

   const toggleAll = (checked: boolean) =>
   {
      let newData = data?.map((item: any) => item?.stac_item);
      if (checked)
      {
         // add tất cả item đang visible (data)
         const newItems = newData.filter(
            (d: any) => !selected.some((p: any) => p.id === d.id),
         );

         dispatch(setSelectedItems([...selected, ...newItems]));
      } else
      {
         // remove tất cả item đang visible
         const remaining = selected.filter(
            (p: any) => !newData.some((d: any) => d.id === p.id),
         );

         dispatch(setSelectedItems(remaining));
      }
   };

   const isAllFootprintShown =
      selectedInView.length > 0 &&
      selectedInView
         ?.filter((item: any) => item?.id)
         ?.every((item: any) => layerIds.has(`footerprint-${item?.id}`));
   const isAllImageShown =
      selectedInView.length > 0 &&
      selectedInView
         ?.filter((item: any) => item?.id)
         ?.every((item: any) => layerIds.has(`image-${item?.id}`));


   const idsImageInCart = useSelector(
      (state: RootState) => state.cartSlice.arrIdImage,
   );

   const performDeleteImages = async (ids: string[]): Promise<void> =>
   {
      setLoading(true);
      try
      {
         // @ts-ignore
         const res = await dispatch(
            // @ts-ignore
            delImgIntoCartAsync({
               cart_item_ids: ids,
            }),
         ).unwrap();

         if (res?.status === 200)
         {
            showSuccess(t("Delete image from cart successfully"));

            // Remove map layers for deleted items
            ids.forEach((id) =>
            {
               if (layerIds.has(`image-${id}`)) removeImageFromMap({ id } as any, dispatch);
               if (layerIds.has(`footerprint-${id}`)) removeFootprint({ id } as any, dispatch);
            });

            // Update Redux cart
            const result = idsImageInCart.filter((id: any) => !ids.includes(id));
            dispatch(setArrIdImage(result));

            // Update local selection
            dispatch(
               setSelectedItems(
                  selected.filter(
                     (itemFilter: any) =>
                        !ids.some((imageId: any) => imageId === itemFilter?.id),
                  ),
               ),
            );

            // Update displayed data
            setData(
               data.filter(
                  (itemFilter: any) =>
                     !ids.some((imageId: any) => imageId === itemFilter?.stac_item_id),
               ),
            );

            setAllData(
               allData.filter(
                  (itemFilter: any) =>
                     !ids.some((imageId: any) => imageId === itemFilter?.stac_item_id),
               ) ?? [],
            );
         } else
         {
            showError(t("Delete image from cart failed"));
         }
      } catch (error)
      {
         showError(t("Delete image from cart failed"));
      } finally
      {
         setLoading(false);
      }
   };

   const handleBulkDelete = async () =>
   {
      toggleConfirmDelete(); // close bulk modal
      const ids = selectedInView?.map((itemM: any) => itemM?.id);
      await performDeleteImages(ids);

   };

   const handleViewInfo = (item?: any) =>
   {
      if (!selected.some((p: any) => p.id === item.id))
      {
         dispatch(
            setSelectedItems([...selected, item],
            ),
         );
      }

      toggleViewInfo();
      setSelectedView(item);
   };

   const handleCardDeleteClick = (item: any) =>
   {
      if (!selected.some((p: any) => p.id === item.id))
      {
         dispatch(
            setSelectedItems([...selected, item],
            ),
         );
      }
      setSingleDeleteItem(item);
      toggleSingleDelete(); // open single delete modal
   };

   const confirmSingleDelete = () =>
   {
      toggleSingleDelete(); // close modal
      if (!singleDeleteItem) return;
      const id = singleDeleteItem?.id;
      performDeleteImages([id]);
      setSingleDeleteItem(null);

   };

   const providerCountMap = useMemo(() =>
   {
      const map: Record<string, number> = {};

      (allData ?? []).forEach((item: any) =>
      {
         const pId = item?.stac_item?.provider?.id;
         if (!pId) return;

         map[pId] = (map[pId] || 0) + 1;
      });

      return map;
   }, [allData]);

   return (
      <MapContext.Provider
         value={{
            engine: engine,
            pluginManager: null,
            mapType: "maplibre",
            mapInstance: engine?.map,
         }}
      >
         <ContentLayoutManagement
            header={<HeaderContent breadcrumb={[{ title: t("Cart") }]} />}
         >
            <div className="w-full h-full flex bg-[var(--neutral-1000)] text-white">
               {/* LEFT PANEL */}

               <div className="w-5/12 h-full bg-[#0F172A] text-white flex flex-col border-r border-gray-800">
                  {/* Header */}
                  <div className="p-6 flex items-center justify-between">
                     <h2 className="text-lg font-semibold">
                        {t("Cart")} ({total})
                     </h2>
                     {/* <Select
                        value={providerId}
                        onChange={(val) => setProviderId(val)}
                        className="div-dropdown !w-[200px]"
                        options={[{ value: "all", label: t("All") }].concat(
                           providers?.map((provider?: any) => ({
                              value: provider.id.toString(),
                              label: provider.name,
                           })) || [],
                        )}
                     /> */}

                     <Select
                        value={providerId}
                        onChange={(val) => setProviderId(val)}
                        className="div-dropdown !w-[200px]"
                        options={[
                           {
                              value: "all",
                              label: `${t("All")} (${allData?.length})`,
                           },
                           ...(providers?.map((provider?: any) => ({
                              value: provider.id.toString(),
                              label: `${provider.name} (${providerCountMap[provider.id] || 0})`,
                           })) || []),
                        ]}
                     />
                  </div>

                  {/* Select all */}
                  <div className="px-6 pb-3 flex items-center justify-between">
                     <Checkbox
                        onChange={(e) => toggleAll(e.target.checked)}
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                     >
                        {t("All")}
                     </Checkbox>
                     {selectedInView.length > 0 && (
                        <div className="flex gap-2 items-center">
                           <span className="mr-2">
                              {selectedInView.length}/{data?.length} {t("Selected")}
                           </span>
                           <Tooltip
                              title={
                                 isAllImageShown
                                    ? t("Hide image on map")
                                    : t("Show image on map")
                              }
                           >
                              <Button
                                 className={`btn-action ${isAllImageShown ? "!bg-[#4CC2FF] !text-black" : ""}`}
                                 icon={<CommonIcons.MapIcon />}
                                 disabled={loading}
                                 onClick={async () =>
                                 {
                                    setLoading(true);
                                    try
                                    {
                                       const promises: Promise<void>[] = [];

                                       selectedInView.forEach((img: any) =>
                                       {
                                          const imageLayerId = `image-${img?.id}`;

                                          if (isAllImageShown)
                                          {
                                             removeImageFromMap(img, dispatch);
                                          } else if (!layerIds.has(imageLayerId))
                                          {
                                             promises.push(
                                                showImageOnMap(
                                                   img,
                                                   dispatch,
                                                   providerId,
                                                   engine?.map,
                                                   true,
                                                ),
                                             );
                                          }
                                       });

                                       await Promise.all(promises);

                                       if (promises.length > 0)
                                       {
                                          flyToGeometries(
                                             selectedInView.map((img: any) => img?.geometry),
                                             engine?.map,
                                          );
                                          showSuccess(t("Images shown on map successfully"));
                                       } else if (isAllImageShown)
                                       {
                                          showSuccess(
                                             t("Images removed from map successfully"),
                                          );
                                       }
                                    } catch (error)
                                    {
                                       showError(t("Failed to update map layers"));
                                       console.error("Map layer error:", error);
                                    } finally
                                    {
                                       setLoading(false);
                                    }
                                 }}
                              />
                           </Tooltip>
                           <Tooltip
                              title={
                                 isAllFootprintShown
                                    ? t("Hide footprint's image on map")
                                    : t("Show footprint's image on map")
                              }
                           >
                              <Button
                                 disabled={loading}
                                 className={`btn-action ${isAllFootprintShown ? "!bg-[#4CC2FF] !text-black" : ""}`}
                                 icon={<CommonIcons.FootIcon />}
                                 onClick={() =>
                                 {
                                    setLoading(true);
                                    try
                                    {
                                       let count = 0;

                                       selectedInView.forEach((img: any) =>
                                       {
                                          const footprintLayerId = `footerprint-${img?.id}`;

                                          if (isAllFootprintShown)
                                          {
                                             removeFootprint(img, dispatch);
                                          } else if (!layerIds.has(footprintLayerId))
                                          {
                                             showFootprint(img, dispatch, engine?.map, true);
                                             count++;
                                          }
                                       });

                                       if (count > 0)
                                       {
                                          flyToGeometries(
                                             selectedInView.map((img: any) => img?.geometry),
                                             engine?.map,
                                          );
                                          showSuccess(
                                             t("Footprints shown on map successfully"),
                                          );
                                       } else if (isAllFootprintShown)
                                       {
                                          showSuccess(
                                             t("Footprints removed from map successfully"),
                                          );
                                       }
                                    } catch (error)
                                    {
                                       showError(t("Failed to update footprint layers"));
                                       console.error("Footprint layer error:", error);
                                    } finally
                                    {
                                       setLoading(false);
                                    }
                                 }}
                              />
                           </Tooltip>

                           <Tooltip title={"Remove from cart"}>
                              <Button
                                 disabled={loading}
                                 className={`btn-action `}
                                 icon={<CommonIcons.DeleteIcon />}
                                 onClick={() => toggleConfirmDelete()}
                              />
                           </Tooltip>
                        </div>
                     )}
                  </div>

                  {/* List */}
                  <Spin
                     className="!overflow-auto"
                     spinning={loading}
                     tip={t("Loading...")}
                     size="large"
                  >
                     <div className="flex-1 overflow-auto px-6 py-2 space-y-3">
                        {data?.map((item?: any) =>
                        {
                           const isSelected =
                              selected?.filter(
                                 (itemF?: any) => itemF?.id === item.stac_item_id,
                              ).length > 0;

                           const isDeleting =
                              (openConfirmDelete && selectedInView.some((s: any) => s.id === item.stac_item_id)) ||
                              (openSingleDelete && singleDeleteItem?.id === item.stac_item_id);

                           return (
                              <CardItemInCart
                                 key={item.stac_item_id}
                                 item={item?.stac_item}
                                 isSelected={isSelected}
                                 isDeleting={isDeleting}
                                 toggleItem={toggleItem}
                                 mapInstance={engine?.map}
                                 onDelete={handleCardDeleteClick}
                                 handleViewInfo={handleViewInfo}
                                 showCheckBox={true}
                              />
                           );
                        })}

                        {data?.length === 0 && !loading && <NoData fontText={"16px"} />}
                     </div>
                  </Spin>

                  {/* Footer */}
                  <div className="p-4 border-t border-gray-800 flex justify-end">
                     <Button
                        disabled={loading || selected.length === 0}
                        className="btn-primary"
                        onClick={() => navigate("/create-order-archive")}
                     >
                        {t("Create Archive Order")}
                     </Button>
                  </div>
               </div>

               {/* RIGHT MAP */}
               <div className="flex-1 relative w-7/12">
                  <UniversalMap
                     mapType="maplibre"
                     showEngine={false}
                     showDraw={false}
                     showDrawAOI={false}
                     showMeasure={false}
                     view={{
                        center: [108.23653393802581, 16.1039225743402],
                        zoom: 5,
                     }}
                     showBaseMap={true}
                     showMapControl={true}
                     onMapReady={setEngine}
                  />
               </div>
            </div>

            {shouldRenderConfirmDelete && (
               <CommonComponents.ModalUI
                  open={openConfirmDelete}
                  width={"350px"}
                  title={t("Confirm")}
                  toggle={() =>
                  {
                     toggleConfirmDelete();
                  }}
                  handleConfirm={handleBulkDelete}
                  loading={loading}
               >
                  <p>
                     {t("Are you sure you want to delete")} {selectedInView.length} {t("images")}?
                  </p>
               </CommonComponents.ModalUI>
            )}

            {shouldRenderSingleDelete && (
               <CommonComponents.ModalUI
                  open={openSingleDelete}
                  width={"350px"}
                  title={t("Confirm")}
                  toggle={() =>
                  {
                     toggleSingleDelete();
                  }}
                  handleConfirm={confirmSingleDelete}
                  loading={loading}
               >
                  <p>{t("Are you sure you want to delete this image?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldRenderViewInfo && (
               <CommonComponents.ModalUI
                  open={openViewInfo}
                  title={t("Information")}
                  toggle={() =>
                  {
                     toggleViewInfo();
                  }}
                  handleConfirm={() => { }}
                  hiddenFooter={true}
                  width={1120}
               >
                  <ImageInfo data={selectedView} open={openViewInfo} />
               </CommonComponents.ModalUI>
            )}
         </ContentLayoutManagement>
      </MapContext.Provider>
   );
}
